import { useRef, useState } from "react";
import { motion } from "framer-motion";
import { X, Download, Loader2, Receipt, MapPin, Calendar, User, CreditCard, Hash } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";

const Row = ({ label, value, icon: Icon, bold }) => {
  if (!value) return null;
  return (
    <div className="flex justify-between items-start py-2.5 border-b border-stone-100 last:border-0">
      <span className="text-stone-500 text-sm flex items-center gap-1.5">
        {Icon && <Icon className="w-3.5 h-3.5" />}
        {label}
      </span>
      <span className={`text-sm text-right max-w-[60%] ${bold ? "font-bold text-emerald-950" : "text-stone-800 font-medium"}`}>
        {value}
      </span>
    </div>
  );
};

const ReceiptModal = ({ receipt, onClose }) => {
  const receiptRef = useRef(null);
  const [downloading, setDownloading] = useState(false);

  if (!receipt) return null;

  const fmtDate = (d) => {
    if (!d) return null;
    try {
      return new Date(d).toLocaleDateString("en-CA", { year: "numeric", month: "long", day: "numeric" });
    } catch { return d; }
  };

  const fmtCurrency = (amt) => {
    if (!amt && amt !== 0) return null;
    return `$${Number(amt).toFixed(2)} ${receipt.currency || "CAD"}`;
  };

  const statusLabel = {
    captured: "Paid", paid: "Paid", authorized: "Authorized",
    released: "Released", pending: "Pending", refunded: "Refunded",
  };

  const handleDownload = async () => {
    if (!receiptRef.current) return;
    setDownloading(true);
    try {
      const canvas = await html2canvas(receiptRef.current, {
        scale: 2,
        backgroundColor: "#ffffff",
        useCORS: true,
        logging: false,
      });
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const pdfW = pdf.internal.pageSize.getWidth();
      const pdfH = (canvas.height * pdfW) / canvas.width;
      pdf.addImage(imgData, "PNG", 0, 0, pdfW, pdfH);
      pdf.save(`Stack-Receipt-${receipt.receipt_id}.pdf`);
      toast.success("Receipt downloaded");
    } catch (err) {
      console.error("PDF generation error:", err);
      toast.error("Failed to download receipt");
    } finally {
      setDownloading(false);
    }
  };

  const subtotal = receipt.amount || 0;
  const fee = receipt.platform_fee || 0;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[60] bg-black/50 flex items-end sm:items-center justify-center"
      onClick={onClose}
      data-testid="receipt-modal-overlay"
    >
      <motion.div
        initial={{ y: 80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 80, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-t-3xl sm:rounded-3xl w-full sm:max-w-md max-h-[92vh] overflow-y-auto shadow-2xl"
        data-testid="receipt-modal"
      >
        {/* Sticky header */}
        <div className="sticky top-0 bg-white z-10 flex items-center justify-between p-4 border-b border-stone-100 rounded-t-3xl">
          <div className="flex items-center gap-2">
            <Receipt className="w-5 h-5 text-emerald-600" />
            <h3 className="font-bold text-emerald-950 text-lg">Receipt</h3>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-stone-100 flex items-center justify-center" data-testid="close-receipt-btn">
            <X className="w-4 h-4 text-stone-600" />
          </button>
        </div>

        {/* Printable receipt content */}
        <div ref={receiptRef} className="bg-white">
          {/* Brand header */}
          <div className="bg-emerald-950 px-6 py-5 text-center">
            <h2 className="text-white text-xl font-bold tracking-wide">STACK</h2>
            <p className="text-emerald-300 text-xs mt-0.5">On-Demand Services</p>
          </div>

          <div className="px-6 py-5 space-y-5">
            {/* Receipt ID + Date */}
            <div className="text-center">
              <p className="text-xs text-stone-400 uppercase tracking-widest font-semibold">Receipt</p>
              <p className="text-lg font-bold text-emerald-950 mt-0.5" data-testid="receipt-id">{receipt.receipt_id}</p>
              {receipt.payment_date && (
                <p className="text-xs text-stone-400 mt-1">{fmtDate(receipt.payment_date)}</p>
              )}
            </div>

            {/* Service */}
            <div className="bg-stone-50 rounded-2xl p-4">
              <p className="text-xs font-semibold text-stone-400 uppercase tracking-wider mb-2">Service</p>
              <p className="font-bold text-emerald-950" data-testid="receipt-service">{receipt.service_label}</p>
            </div>

            {/* Details */}
            <div>
              <p className="text-xs font-semibold text-stone-400 uppercase tracking-wider mb-1">Details</p>
              <Row label="Customer" value={receipt.customer_name} icon={User} />
              <Row label="Email" value={receipt.customer_email} />
              {receipt.professional_name && (
                <Row label="Professional" value={receipt.professional_name} icon={User} />
              )}
              <Row label="Location" value={receipt.location} icon={MapPin} />
              {(receipt.service_date || receipt.service_time) && (
                <Row label="Date & Time" value={[receipt.service_date, receipt.service_time].filter(Boolean).join(" ")} icon={Calendar} />
              )}
            </div>

            {/* Payment breakdown */}
            <div>
              <p className="text-xs font-semibold text-stone-400 uppercase tracking-wider mb-1">Payment</p>
              <Row label="Status" value={statusLabel[receipt.payment_status] || receipt.payment_status} icon={CreditCard} />
              <Row label="Method" value={receipt.payment_method} />
              {receipt.transaction_id && (
                <Row label="Transaction" value={receipt.transaction_id.substring(0, 18) + "..."} icon={Hash} />
              )}

              {/* Amount box */}
              {subtotal > 0 && (
                <div className="bg-emerald-50 rounded-xl p-4 mt-3 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-stone-600">Subtotal</span>
                    <span className="font-medium text-stone-800">{fmtCurrency(subtotal)}</span>
                  </div>
                  {fee > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-stone-600">Service fee</span>
                      <span className="font-medium text-stone-800">{fmtCurrency(fee)}</span>
                    </div>
                  )}
                  <div className="border-t border-emerald-200 pt-2 flex justify-between">
                    <span className="font-bold text-emerald-950">Total</span>
                    <span className="font-bold text-emerald-700 text-lg" data-testid="receipt-total">{fmtCurrency(subtotal)}</span>
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="text-center pt-2 pb-1 border-t border-dashed border-stone-200">
              <p className="text-xs text-stone-400">Thank you for using Stack!</p>
              <p className="text-xs text-stone-300 mt-0.5">support@stackservices.com</p>
            </div>
          </div>
        </div>

        {/* Download button (outside printable area) */}
        <div className="px-6 pb-6 pt-2">
          <Button
            className="w-full bg-emerald-950 hover:bg-emerald-900 text-white rounded-xl h-12"
            onClick={handleDownload}
            disabled={downloading}
            data-testid="download-receipt-btn"
          >
            {downloading ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating PDF...</>
            ) : (
              <><Download className="w-4 h-4 mr-2" /> Download Receipt</>
            )}
          </Button>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default ReceiptModal;
